/* Bhairav Tatva v11 — gemstone collection filters that see the whole collection.
   Replaces initFilters in bt.js for sections marked data-bt-gfilters (bt.js only looks at data-bt-filters).
   Collections over 250 stones (Gemstones has ~1,800, Emerald ~300): the page carries the first 250 cards,
   the rest come in as compact JSON from sections/bt-gem-feed.liquid, so the Gemstone / price / shape …
   options, counts and sorting cover every stone. Cards for those are drawn only when they are shown. */
(() => {
  const inrShort = (n) => {
    if (n >= 100000) return `₹${String(+(n / 100000).toFixed(2)).replace(/\.0+$/, '')} L`;
    return `₹${Math.round(n).toLocaleString('en-IN')}`;
  };
  const parseBuckets = (str) =>
    (str || '').split(',').map((s) => s.trim()).filter(Boolean).map((s) => {
      const [a, b] = s.split('-');
      return { key: s, min: parseFloat(a) || 0, max: b === undefined || b === '' ? Infinity : parseFloat(b) };
    });
  const rangeLabel = (g, b) => {
    const unit = g === 'carat' ? ' ct' : g === 'ratti' ? ' ratti' : '';
    const f = g === 'price' ? inrShort : (n) => String(n);
    if (b.min === 0 && b.max !== Infinity) return `Under ${f(b.max)}${unit}`;
    if (b.max === Infinity) return `${f(b.min)}${unit}+`;
    return g === 'price' ? `${f(b.min)} – ${f(b.max)}` : `${b.min} – ${b.max}${unit}`;
  };

  // Renders a card from the bt-gem-feed JSON. Markup mirrors snippets/bt-gem-card.liquid.
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const CARD_SIZES = '(min-width: 1200px) 22vw, (min-width: 750px) 30vw, 46vw';
  const srcset = (u) => [320, 480, 720].map((w) => `${u.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');
  function cardFromFeed(d) {
    const li = document.createElement('li');
    li.className = 'bt-gcard';
    const swap = !d.v && d.i2;
    let media = '';
    if (d.i) {
      media += `<img src="${esc(d.i)}" srcset="${esc(srcset(d.i))}" sizes="${CARD_SIZES}" width="${d.w || 720}" height="${d.h || 720}" loading="lazy" alt="${esc(d.alt)}" class="bt-gcard__img">`;
      if (swap) media += `<img src="${esc(d.i2)}" srcset="${esc(srcset(d.i2))}" sizes="${CARD_SIZES}" width="720" height="720" loading="lazy" alt="" class="bt-gcard__img bt-gcard__img--alt">`;
    }
    if (d.v) media += `<button type="button" class="bt-gcard__vid" aria-label="Play video of ${esc(d.t)}"><span class="bt-gcard__vid-ico" aria-hidden="true"><svg width="9" height="9" viewBox="0 0 18 18"><path d="M5 3.5v11l9-5.5z" fill="currentColor"/></svg></span><span class="bt-gcard__vid-play">Play video</span><span class="bt-gcard__vid-load">Loading video</span><span class="bt-gcard__vid-err">Try again</span><span class="bt-gcard__vid-stop">Stop</span></button>`;
    if (!d.a) media += '<span class="bt-card__badge">Sold out</span>';
    li.innerHTML = `<a class="bt-gcard__link" href="${esc(d.u)}"><div class="bt-gcard__media${swap ? ' bt-gcard__media--swap' : ''}"${d.v ? ` data-vimeo="${esc(d.v)}"` : ''}>${media}</div><div class="bt-gcard__body"><h3 class="bt-gcard__title">${esc(d.t)}${d.cf ? `<span class="bt-gcard__ct"> – ${esc(d.cf)} carat</span>` : ''}</h3><div class="bt-gcard__row"><p class="bt-gcard__price">${esc(d.pf)}</p>${d.s ? `<p class="bt-gcard__sku"><span>SKU</span> ${esc(d.s)}</p>` : ''}</div></div></a>`;
    return li;
  }
  // Every data-* on a card becomes a filterable field (gem cards: shape, origin…; Rudraksha cards: mukhi, intention, planet…)
  const NUMERIC = ['price', 'carat', 'ratti', 'order'];
  const itemFromEl = (el) => {
    const it = { el };
    Object.entries(el.dataset).forEach(([k, v]) => { it[k] = NUMERIC.includes(k) ? Number(v) || 0 : v || ''; });
    NUMERIC.forEach((k) => { if (!(k in it)) it[k] = 0; });
    return it;
  };
  const itemFromFeed = (d, order) => ({
    el: null,
    feed: d,
    price: Number(d.p) || 0,
    carat: Number(d.ct) || 0,
    ratti: Number(d.rt) || 0,
    shape: d.sh || '',
    origin: d.o || '',
    treatment: d.tr || '',
    type: d.ty || '',
    order,
  });
  const elFor = (it) => {
    if (!it.el) { it.el = cardFromFeed(it.feed); it.el.hidden = true; }
    return it.el;
  };

  function initFilters(root) {
    const grid = root.querySelector('[data-fl-grid]');
    if (!grid) return;
    root.classList.add('is-ready');
    const pageSize = Number(root.dataset.pageSize) || 24;
    const items = Array.from(grid.querySelectorAll('.bt-gcard')).map(itemFromEl);
    if (!items.length) { root.classList.add('bt-gcol--empty'); return; }

    const buckets = { price: parseBuckets(root.dataset.price), carat: parseBuckets(root.dataset.carat), ratti: parseBuckets(root.dataset.ratti) };
    const groups = Array.from(root.querySelectorAll('.bt-fl__group')).map((fs) => ({ fs, name: fs.dataset.group, kind: fs.dataset.kind }));
    const state = {};
    groups.forEach((g) => (state[g.name] = new Set()));
    let sort = 'new';
    let shown = pageSize;

    const findBucket = (g, key) => buckets[g].find((b) => b.key === key);
    const match = (it, g, val) => {
      if (buckets[g]) { const b = findBucket(g, val); return !!b && it[g] > 0 && it[g] >= b.min && it[g] < b.max; }
      return it[g] === val;
    };
    const passes = (it, except) => groups.every((g) => {
      if (g.name === except || !state[g.name].size) return true;
      for (const v of state[g.name]) if (match(it, g.name, v)) return true;
      return false;
    });
    const labelFor = (g, v) => (buckets[g] ? rangeLabel(g, findBucket(g, v) || { min: 0, max: Infinity }) : v);

    // Options come from every stone in the collection (rebuilt once the rest of the collection has loaded)
    function buildOptions() {
      groups.forEach((g) => {
        let values;
        if (buckets[g.name]) values = buckets[g.name].filter((b) => items.some((it) => match(it, g.name, b.key))).map((b) => b.key);
        else values = Array.from(new Set(items.map((it) => it[g.name]).filter(Boolean))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        g.values = values;
        const box = g.fs.querySelector('.bt-fl__opts');
        box.innerHTML = '';
        g.inputs = null;
        g.fs.hidden = values.length < 2;
        if (g.fs.hidden) return;
        g.inputs = values.map((v) => {
          const lab = document.createElement('label');
          lab.className = 'bt-fl__opt';
          const inp = document.createElement('input');
          inp.type = 'checkbox';
          inp.value = v;
          inp.addEventListener('change', () => {
            if (inp.checked) state[g.name].add(v); else state[g.name].delete(v);
            shown = pageSize;
            apply();
          });
          const t = document.createElement('span');
          t.textContent = labelFor(g.name, v);
          const n = document.createElement('em');
          lab.append(inp, t, n);
          box.appendChild(lab);
          return { inp, n, lab, v };
        });
        g.fs.classList.toggle('bt-fl__group--chips', g.kind === 'range');
      });
      buildChips();
    }

    // Quick chips above the grid (e.g. 1 Mukhi · 3 Mukhi …): one tap = that value only; "All" clears the group
    const chipBox = root.querySelector('[data-fl-chips]');
    function buildChips() {
      if (!chipBox) return;
      const g = groups.find((x) => x.name === chipBox.dataset.flChips);
      if (!g || !g.values) return;
      chipBox.innerHTML = '';
      const make = (value, label, img) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bt-chip';
        b.dataset.value = value;
        if (img) {
          const i = document.createElement('img');
          i.src = img; i.alt = ''; i.width = 28; i.height = 28; i.loading = 'lazy';
          b.appendChild(i);
        }
        const t = document.createElement('span');
        t.textContent = label;
        b.appendChild(t);
        b.addEventListener('click', () => {
          state[g.name].clear();
          if (value) state[g.name].add(value);
          shown = pageSize;
          apply();
        });
        chipBox.appendChild(b);
      };
      make('', chipBox.dataset.allLabel || 'All');
      g.values.forEach((v) => {
        const first = items.find((it) => it[g.name] === v && it.chipImg);
        make(v, v, first && first.chipImg);
      });
    }
    const syncChips = () => {
      if (!chipBox) return;
      const set = state[chipBox.dataset.flChips];
      if (!set) return;
      chipBox.querySelectorAll('.bt-chip').forEach((b) => {
        const v = b.dataset.value;
        const on = v ? set.size === 1 && set.has(v) : set.size === 0;
        b.classList.toggle('is-current', on);
        b.setAttribute('aria-pressed', String(on));
      });
    };

    // Restore from URL (values that are not in the collection yet are kept until the full list arrives)
    const params = new URLSearchParams(location.search);
    const restore = () => groups.forEach((g) => {
      const raw = params.get(g.name);
      if (raw && g.values) raw.split(',').forEach((v) => { if (g.values.includes(v)) state[g.name].add(v); });
    });
    if (params.get('sort')) sort = params.get('sort');
    const sortSel = root.querySelector('[data-fl-sort]');
    if (sortSel) {
      sortSel.value = sort;
      sortSel.addEventListener('change', () => { sort = sortSel.value; apply(); });
    }

    const counts = root.querySelectorAll('[data-fl-count]');
    const badge = root.querySelector('[data-fl-badge]');
    const active = root.querySelector('[data-fl-active]');
    const empty = root.querySelector('[data-fl-empty]');
    const more = root.querySelector('[data-fl-more]');
    const moreBtn = more && (more.querySelector('[data-fl-more-btn]') || more.querySelector('button'));
    const shownEl = root.querySelector('[data-fl-shown]');
    const bar = root.querySelector('[data-fl-progress]');
    if (moreBtn) moreBtn.addEventListener('click', () => { shown += pageSize; apply(false); });
    // One control only: hide the plain page links (kept in the HTML for crawlers / no-JS)
    root.querySelectorAll('[data-fl-pages], .bt-gcol__pages').forEach((n) => { n.hidden = true; });
    const fmt = (n) => n.toLocaleString('en-IN');
    const noun = root.dataset.noun || 'stones';

    const sorters = {
      new: (a, b) => a.order - b.order,
      'price-asc': (a, b) => a.price - b.price,
      'price-desc': (a, b) => b.price - a.price,
      'carat-desc': (a, b) => b.carat - a.carat,
      'carat-asc': (a, b) => a.carat - b.carat,
      'order-desc': (a, b) => b.order - a.order,
    };

    function apply(updateUrl = true) {
      const list = items.filter((it) => passes(it)).sort(sorters[sort] || sorters.new);
      const set = new Set(list);
      const frag = document.createDocumentFragment();
      list.forEach((it, k) => {
        if (k < shown) { const el = elFor(it); el.hidden = false; frag.appendChild(el); }
        else if (it.el) { it.el.hidden = true; frag.appendChild(it.el); }
      });
      items.forEach((it) => { if (!set.has(it) && it.el) { it.el.hidden = true; frag.appendChild(it.el); } });
      grid.appendChild(frag);

      counts.forEach((c) => (c.textContent = list.length));
      if (empty) empty.hidden = list.length > 0;
      if (more) {
        const vis = Math.min(shown, list.length);
        more.hidden = list.length === 0;
        if (moreBtn) moreBtn.hidden = list.length <= shown;
        if (shownEl) shownEl.textContent = list.length <= shown ? `Showing all ${fmt(list.length)} ${noun}` : `Showing ${fmt(vis)} of ${fmt(list.length)} ${noun}`;
        if (bar) bar.style.width = `${list.length ? Math.round((vis / list.length) * 100) : 0}%`;
      }

      let n = 0;
      active.innerHTML = '';
      groups.forEach((g) => {
        if (!g.inputs) return;
        g.inputs.forEach((o) => {
          const c = items.filter((it) => passes(it, g.name) && match(it, g.name, o.v)).length;
          o.n.textContent = c;
          o.inp.checked = state[g.name].has(o.v);
          o.lab.classList.toggle('is-off', c === 0 && !o.inp.checked);
          if (o.inp.checked) {
            n++;
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'bt-pill';
            pill.innerHTML = '<span></span><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 2l6 6M8 2 2 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
            pill.firstChild.textContent = labelFor(g.name, o.v);
            pill.setAttribute('aria-label', `Remove ${labelFor(g.name, o.v)}`);
            pill.addEventListener('click', () => { state[g.name].delete(o.v); shown = pageSize; apply(); });
            active.appendChild(pill);
          }
        });
      });
      if (n) {
        const clr = document.createElement('button');
        clr.type = 'button';
        clr.className = 'bt-pill bt-pill--clear';
        clr.textContent = 'Clear all';
        clr.addEventListener('click', clearAll);
        active.appendChild(clr);
      }
      if (badge) { badge.hidden = !n; badge.textContent = n; }
      syncChips();

      if (updateUrl) {
        const p = new URLSearchParams(location.search);
        groups.forEach((g) => { if (state[g.name].size) p.set(g.name, Array.from(state[g.name]).join(',')); else p.delete(g.name); });
        if (sort !== 'new') p.set('sort', sort); else p.delete('sort');
        const q = p.toString();
        history.replaceState(null, '', location.pathname + (q ? `?${q}` : ''));
      }
      document.dispatchEvent(new CustomEvent('bt:grid'));
    }
    function clearAll() { groups.forEach((g) => state[g.name].clear()); shown = pageSize; apply(); }
    root.querySelectorAll('[data-fl-clear]').forEach((b) => b.addEventListener('click', clearAll));

    const open = () => { root.classList.add('is-fl-open'); document.documentElement.classList.add('bt-lock'); };
    const close = () => { root.classList.remove('is-fl-open'); document.documentElement.classList.remove('bt-lock'); };
    root.querySelectorAll('[data-fl-open]').forEach((b) => b.addEventListener('click', open));
    root.querySelectorAll('[data-fl-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    buildOptions();
    restore();
    apply(false);

    // Collections over 250 stones: the page only carries the first 250 cards, so fetch the rest as
    // compact JSON (sections/bt-gem-feed.liquid) and let the filters, counts and sorting see all of them.
    const pages = Number(grid.dataset.pages) || 1;
    const per = Number(grid.dataset.per) || 250;
    if (pages < 2 || Number(grid.dataset.page) > 1) return;
    root.classList.add('is-loading-all');
    const url = (k) => `${location.pathname}?section_id=bt-gem-feed&page=${k}`;
    const fetchPage = (k) => fetch(url(k), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const m = html.match(/<script[^>]*data-bt-feed[^>]*>([\s\S]*?)<\/script>/);
        return m ? JSON.parse(m[1]) : [];
      });
    const ks = [];
    for (let k = 2; k <= pages; k++) ks.push(k);
    Promise.all(ks.map((k) => fetchPage(k).then((rows) => rows.map((d, i) => itemFromFeed(d, (k - 1) * per + i + 1))).catch(() => [])))
      .then((chunks) => {
        chunks.forEach((c) => items.push(...c));
        const keep = {};
        groups.forEach((g) => (keep[g.name] = new Set(state[g.name])));
        buildOptions();
        groups.forEach((g) => {
          state[g.name] = new Set(Array.from(keep[g.name]).filter((v) => g.values.includes(v)));
        });
        restore();
        apply(false);
        document.dispatchEvent(new CustomEvent('bt:filters-rebuilt'));
      })
      .finally(() => root.classList.remove('is-loading-all'));
  }

  const boot = () => document.querySelectorAll('[data-bt-gfilters]:not(.is-ready)').forEach(initFilters);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
